import {PUNISHMENT_KEYS} from "@/api/punishmentApi";
import RightChevron from "@/assets/colorable/right_chevron.svg?react";
import RightArrowIcon from "@/assets/light_gradient/right_arrow.svg?react";
import AnimatedAccordion from "@/components/Common/animated/AnimatedAccordion";
import Button from "@/components/Common/Button";
import Loader from "@/components/Common/kpi/Loader";
import DisciplineMark from "@/components/discipline/DisciplineMark";
import TimerDisciplineMark from "@/components/discipline/TimerDisciplineMark";
import type {PunishmentDetailSerializer} from "@/generated/Punishment";
import {useDiscipline} from "@/hooks/useDiscipline";
import {useInViewWithQuery} from "@/hooks/useInViewWithQuery";
import {useGenericInfiniteQuery} from "@/hooks/useQueryHooks";
import clsxm from "@/lib/clsxm";
import {FULL_DATE_FORMAT} from "@/store/constants";
import {
  disciplinaryModalAtom,
  isDisciplinaryModalOpenAtom,
  isSingleDisciplineModalOpenAtom,
  isTriggeredFromDisciplinaryModalAtom,
} from "@/store/jotaiAtoms";
import {getRemainingTime, isDisciplineActive, isDisciplineOngoing, isUnverifiedType} from "@/utils/disciplineUtils";
import dayjs from "dayjs";
import {useAtom, useSetAtom} from "jotai";
import {useCallback, useState, type FC} from "react";
import {useTranslation} from "react-i18next";
import {Tooltip} from "react-tooltip";

const DisciplineHistory: FC<{userId: number}> = ({userId}) => {
  const [isOpen, setOpen] = useState(true);
  const setDisciplinaryData = useSetAtom(disciplinaryModalAtom);
  const {getLabelByDisciplineType, getUnverificationPlatformLabel} = useDiscipline();
  const {t} = useTranslation("influencer_discipline");
  const setSingleDisciplineModalOpen = useSetAtom(isSingleDisciplineModalOpenAtom);
  const [isDisciplinaryModalOpen, setDisciplinaryModalOpen] = useAtom(isDisciplinaryModalOpenAtom);
  const setTriggeredFromDisciplinaryModal = useSetAtom(isTriggeredFromDisciplinaryModalAtom);

  const punishmentsQuery = useGenericInfiniteQuery<PunishmentDetailSerializer>({
    queryKey: PUNISHMENT_KEYS.qPunishments(userId),
  });

  const {ref: queryRef} = useInViewWithQuery(punishmentsQuery);

  const openSingleDisciplineModal = useCallback(
    (discipline: PunishmentDetailSerializer) => {
      setDisciplinaryData(draft => {
        draft.userId = userId;
        draft.selectedDiscipline = discipline;
        draft.disciplineDuration = getRemainingTime(discipline.expiration_date).remainingDays;
      });

      if (isDisciplinaryModalOpen) {
        setTriggeredFromDisciplinaryModal(true);
      }

      setSingleDisciplineModalOpen(true);
      setDisciplinaryModalOpen(false);
    },
    [
      isDisciplinaryModalOpen,
      setDisciplinaryData,
      setDisciplinaryModalOpen,
      setSingleDisciplineModalOpen,
      setTriggeredFromDisciplinaryModal,
    ],
  );

  return (
    <div className="w-full">
      <div className="flex cursor-pointer items-center justify-between py-2" onClick={() => setOpen(!isOpen)}>
        <span className="text-neutral-black text-sm font-semibold">
          {t("history_title", {count: punishmentsQuery.data?.length})}
        </span>
        <RightChevron
          className={(isOpen ? "-rotate-90" : "rotate-90 ") + " text-neutral-black size-3 transition-all"}
        />
      </div>
      <AnimatedAccordion condition={isOpen}>
        <div className="w-full overflow-x-auto">
          <div className="min-w-2xl lg:min-w-full">
            <div className="text-neutral-grey-dark bg-neutral-grey-lightest border-neutral-white-dark grid grid-cols-14 items-center gap-3 px-3 py-1.5 text-xs font-semibold uppercase sm:grid-cols-13">
              <span className="col-span-5 sm:col-span-4">{t("disciplinary_action")}</span>
              <span className="col-span-4 pl-3">{t("campaign")}</span>
              <span className="col-span-4 pl-3">{t("from_to")}</span>
              <span className="col-span-1 flex justify-end pl-3">{t("view")}</span>
            </div>
            <div className="max-h-55 overflow-y-auto">
              {punishmentsQuery.data?.map((discipline, index, array) => {
                const isActive = isDisciplineActive(discipline);

                return (
                  <div
                    key={index}
                    className="border-b-neutral-white-dark grid grid-cols-14 border-b-1 px-3 py-1.5 sm:grid-cols-13">
                    <div className="col-span-5 flex items-center gap-2 sm:col-span-4">
                      {isActive ? (
                        <TimerDisciplineMark
                          discipline={discipline}
                          type={discipline.punishment_type}
                          onClick={() => openSingleDisciplineModal(discipline)}
                        />
                      ) : (
                        <DisciplineMark type={discipline.punishment_type} />
                      )}
                      <span className="text-neutral-black font-semibold">
                        {isUnverifiedType(discipline.punishment_type)
                          ? t("platform_unverified", {
                              platform: getUnverificationPlatformLabel(discipline.punishment_type),
                            })
                          : getLabelByDisciplineType(discipline.punishment_type)}
                      </span>
                    </div>
                    <div className="col-span-4 flex items-center pl-3">
                      <span className="text-primary-dark-solid font-semibold">
                        {discipline.campaign_application?.campaign.title}
                      </span>
                    </div>
                    <div className="col-span-4 flex items-center gap-2.5 pl-3">
                      <div
                        className={clsxm(
                          "border-neutral-grey-light-dark bg-neutral-grey-light size-3 rounded-full",
                          isActive && "bg-success-medium-green",
                        )}
                      />
                      <div className="text-neutral-black text-sm font-semibold">
                        <span>{dayjs(discipline.effective_date).format(FULL_DATE_FORMAT)}</span>
                        <span>{` - ${isDisciplineOngoing(discipline) ? t("ongoing") : dayjs(discipline.expiration_date).format(FULL_DATE_FORMAT)}`}</span>
                      </div>
                    </div>
                    <div className="col-span-1 flex items-center justify-end pl-3">
                      <Button
                        data-tooltip-id={`disciplinary-${discipline?.id}-view-discipline`}
                        onClick={() => openSingleDisciplineModal(discipline)}
                        buttonStyle="White"
                        className="size-8">
                        <RightArrowIcon className="size-2.5 shrink-0" />
                      </Button>
                      <Tooltip
                        id={`disciplinary-${discipline?.id}-view-discipline`}
                        variant="dark"
                        place="bottom"
                        className="z-50">
                        {t("view_discipline")}
                      </Tooltip>
                    </div>
                    {index === array.length - 1 && <div ref={queryRef} />}
                  </div>
                );
              })}
              {punishmentsQuery.isFetching && <Loader styles={{loaderClassName: "size-6 align-center my-5"}} />}
            </div>
          </div>
        </div>
      </AnimatedAccordion>
    </div>
  );
};

export default DisciplineHistory;
